import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface SprintInvitationAcceptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notificationId: string;
  sprintId: string;
  sprintObjective: string;
  inviterName: string;
  customMessage?: string;
  invitationId: string;
}

export const SprintInvitationAcceptDialog = ({
  open,
  onOpenChange,
  notificationId,
  sprintId,
  sprintObjective,
  inviterName,
  customMessage,
  invitationId,
}: SprintInvitationAcceptDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const handleAccept = async () => {
    setIsProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update sprint_invitations status to 'accepted'
      const { error: invitationError } = await supabase
        .from('sprint_invitations')
        .update({ 
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          invitee_user_id: user.id
        })
        .eq('id', invitationId);

      if (invitationError) throw invitationError;

      // Add user to sprint_participants
      const { error: participantError } = await supabase
        .from('sprint_participants')
        .insert({
          sprint_id: sprintId,
          user_id: user.id,
          role: 'contributor'
        });

      if (participantError) throw participantError;

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      toast({
        title: "Sprint joined!",
        description: "You've successfully joined the sprint.",
      });

      onOpenChange(false);
      navigate(`/prompt-architecture-sprint?sprint=${sprintId}`);

    } catch (error) {
      console.error('Error accepting sprint invitation:', error);
      toast({
        title: "Failed to join sprint",
        description: "There was an error joining the sprint. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!showDeclineForm) {
      setShowDeclineForm(true);
      return;
    }

    setIsProcessing(true);
    
    try {
      // Update sprint_invitations status to 'declined' with reason
      const { error: invitationError } = await supabase
        .from('sprint_invitations')
        .update({ 
          status: 'declined',
          decline_reason: declineReason || null
        })
        .eq('id', invitationId);

      if (invitationError) throw invitationError;

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      // Send notification to inviter about the decline
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user?.id)
        .single();
      
      // Get inviter's user id from the invitation
      const { data: invitation } = await supabase
        .from('sprint_invitations')
        .select('inviter_id')
        .eq('id', invitationId)
        .single();

      if (invitation?.inviter_id) {
        await supabase.from('notifications').insert({
          user_id: invitation.inviter_id,
          type: 'sprint_invitation_declined',
          title: 'Sprint Invitation Declined',
          message: `${profile?.display_name || 'A user'} declined your sprint invitation${declineReason ? `: "${declineReason}"` : '.'}`,
          action_url: `/prompt-architecture-sprint?sprint=${sprintId}`,
          metadata: {
            sprint_id: sprintId,
            decliner_name: profile?.display_name,
            decline_reason: declineReason
          }
        });
      }

      toast({
        title: "Invitation declined",
        description: "You've declined the sprint invitation.",
      });

      onOpenChange(false);

    } catch (error) {
      console.error('Error declining sprint invitation:', error);
      toast({
        title: "Failed to decline invitation",
        description: "There was an error processing your response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (showDeclineForm) {
      setShowDeclineForm(false);
      setDeclineReason("");
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Sprint Invitation</DialogTitle>
          <DialogDescription>
            You've been invited to join a sprint
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">From:</p>
            <p className="text-base font-semibold">{inviterName}</p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Sprint Objective:</p>
            <p className="text-base">{sprintObjective}</p>
          </div>

          {customMessage && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Personal Message:</p>
              <div className="p-3 bg-muted rounded-md">
                <p className="text-sm italic">{customMessage}</p>
              </div>
            </div>
          )}

          {showDeclineForm ? (
            <div className="space-y-2">
              <Label htmlFor="decline-reason">Reason for declining (optional)</Label>
              <Textarea
                id="decline-reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Let the inviter know why you're declining..."
                rows={3}
              />
            </div>
          ) : (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground">
                Would you like to join this sprint and contribute to achieving this objective?
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {showDeclineForm ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Confirm Decline
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={isProcessing}
              >
                <XCircle className="mr-2 h-4 w-4" />
                No, Decline
              </Button>
              <Button
                onClick={handleAccept}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Yes, Join Sprint
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};